import { PromiseUtil } from '../../async/async';
import { readWholeStream } from '../../util/readableStream';
import { invariant } from '../../util/invariant';
import type { ResourceAccessor, ResourceLocation } from '../../io/resource';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import type { IndexTableSegment } from './indexTableSegment';
import { parseIndexTableSegments } from './indexTableSegment';
import type { MimirIndex, MimirIndexFile } from './mimirIndexFile';
import { parseMimirIndexFile, getFirstFrameOffset, getLastFrameEndOffset } from './mimirIndexFile';
import type { PartitionPackAndHeaders } from './reconstructionUtils';
import { readPartitionPackAndHeaders, failOnSecondEncounteredNonFooterPartition, deriveFooterFromHeader, completeTransform } from './reconstructionUtils';
import { bytesToKlv } from './klv';
import type { MxfRewrapInput } from './rewrapInputs';
import { exceptionToUserFriendlyMessage } from '@mimir/common/util/exceptionToUserFriendlyMessage';
import { NO_LOGGER } from '@mimir/common/util/logging';
import { safeArrayAccess } from '@mimir/common/util/safeArrayAccess';

export interface MxfRewrapSource {
  highRes: ResourceLocation
  /**
   *  First frame to include from this MXF
   */
  inPoint: number,

  /**
   *  Last frame to include from this MXF + 1
   */
  outPoint: number,
  index: MimirIndex,
  compatibilityHash: string | null,
  fileSize: number,
  origin: number,
}

export interface MxfRewrapTemplates {
  header: PartitionPackAndHeaders,
  indexTableSegment: IndexTableSegment,
  footer: PartitionPackAndHeaders,
}

export interface MxfRewrapDependencies {
  sources: MxfRewrapSource[],
  templates: MxfRewrapTemplates,
}

export async function fetchMxfRewrapDependencies(
  services: {
    logger: StructuredLogger,
    resourceAccessor: ResourceAccessor,
  },
  sources: MxfRewrapInput[],
): Promise<MxfRewrapDependencies> {
  const { resourceAccessor } = services;

  const fetchIndexPromises = new PromiseUtil<void>();

  const primitiveKeyMap: Record<string, { key: string } | undefined> = {};
  type ResourceKey = ResourceLocation | { key: string };
  /**
   * Returns an object that can be used in a WeakMap to uniquely identify a resource location. For locations with
   * primitive values, the key is an object containing a string key. For locations with functions or objects as values,
   * the key is the resource location itself.
   */
  function resourceKey(resource: ResourceLocation): ResourceKey {
    switch (resource.type) {
      case 'http': {
        const key = `http|${resource.provider.getUrl()}`;
        const keyObject = primitiveKeyMap[key];
        if (keyObject) {
          return keyObject;
        }
        const newKeyObject = { key };
        primitiveKeyMap[key] = newKeyObject;
        return newKeyObject;
      }

      case 'file_ranges': {
        const key = JSON.stringify(resource.ranges.map(r => r.path));
        const keyObject = primitiveKeyMap[key];
        if (keyObject) {
          return keyObject;
        }
        const newKeyObject = { key };
        primitiveKeyMap[key] = newKeyObject;
        return newKeyObject;
      }

      case 'file': {
        const key = `file|${resource.filename}`;
        const keyObject = primitiveKeyMap[key];
        if (keyObject) {
          return keyObject;
        }
        const newKeyObject = { key };
        primitiveKeyMap[key] = newKeyObject;
        return newKeyObject;
      }

      case 'memory':
        return resource;
    }
  }

  const indicesMap = new WeakMap<ResourceKey, MimirIndexFile | null>();
  for (const source of sources) {
    const indexKey = resourceKey(source.index);
    if (indicesMap.has(indexKey)) {
      continue;
    }
    indicesMap.set(indexKey, null);
    fetchIndexPromises.push(
      fetchIndex(services, source.index)
        .then(index => {
          indicesMap.set(indexKey, index);
        })
    );
  }

  await fetchIndexPromises.waitForAll();

  const firstItem = safeArrayAccess(sources, 0);
  const firstIndex = indicesMap.get(resourceKey(firstItem.index));

  invariant(firstIndex, 'expected first index');

  const templates = await fetchTemplates(services, firstItem.highRes, firstIndex);

  const sizePromises = new PromiseUtil<void>();
  const sizeMap = new WeakMap<ResourceKey, number>();
  for (const source of sources) {
    const sizeKey = resourceKey(source.highRes);
    if (sizeMap.has(sizeKey)) {
      continue;
    }
    sizeMap.set(sizeKey, 0);
    sizePromises.push(
      resourceAccessor.getSize(source.highRes)
        .then(size => {
          sizeMap.set(sizeKey, size);
        })
    );
  }
  await sizePromises.waitForAll();

  return {
    sources: sources.map((source) => {
      const index = indicesMap.get(resourceKey(source.index));
      invariant(index, 'expected indexFile');
      const fileSize = sizeMap.get(resourceKey(source.highRes));
      invariant(fileSize, 'expected fileSize');
      return {
        inPoint: source.inPoint,
        outPoint: source.outPoint,
        highRes: source.highRes,
        index: index.index,
        compatibilityHash: source.compatibilityHash,
        fileSize: fileSize,
        origin: source.origin,
      };
    }),
    templates,
  };
}

async function fetchTemplates(
  services: {
    logger: StructuredLogger,
    resourceAccessor: ResourceAccessor,
  },
  highRes: ResourceLocation,
  mimirIndexFile: MimirIndexFile,
): Promise<MxfRewrapTemplates> {
  const { resourceAccessor, logger } = services;
  const headerStart = 0;
  const headerEnd = getFirstFrameOffset(mimirIndexFile);

  const header =
    await completeTransform(
      await resourceAccessor.open(highRes, headerStart, headerEnd),
      'MXF header',
      (readable) => {
        return readable
          .pipeThrough(bytesToKlv(logger))
          .pipeThrough(readPartitionPackAndHeaders());
      }
    );

  const indexTableSegmentStart = mimirIndexFile.header.firstIndexTableOffset;
  const indexTableSegmentEnd = indexTableSegmentStart + mimirIndexFile.header.firstIndexTableSize;
  const indexTableSegment =
    await completeTransform(
      await resourceAccessor.open(highRes, indexTableSegmentStart, indexTableSegmentEnd),
      'MXF index table segment',
      (readable) => {
        return readable
          .pipeThrough(bytesToKlv(logger))
          .pipeThrough(parseIndexTableSegments(logger));
      }
    );


  let footer : PartitionPackAndHeaders | null = null;
  const lastFrameOffset = getLastFrameEndOffset(mimirIndexFile);
  if (lastFrameOffset !== null) {
    logger.log({ message: `Fetching footer with offset: ${lastFrameOffset}`, level: 'info' });

    footer =
      await completeTransform(
        await resourceAccessor.open(highRes, lastFrameOffset, null),
        'MXF footer',
        (readable) => {
          return readable
            .pipeThrough(bytesToKlv(logger))
            .pipeThrough(readPartitionPackAndHeaders())
            // The idea here is that if the MXF file and index is complete then
            // we can find the footer by looking for the first partition after
            // the last frame. The problem is that if the last frame reported
            // in the index is NOT the last frame then we may end up reading
            // through the entire MXF file trying to find the footer. Because
            // of this we have this sanity that if we encounter a non-footer
            // partition then we are probably dealing with an incomplete index
            // and MXF. Additionally even some complete MXF files will have a
            // trailing body partition with 0 frames that only contain the
            // index for the previous partition. Because of this we also need
            // to allow encountering one body partition before bailing out.
            .pipeThrough(failOnSecondEncounteredNonFooterPartition());
        }
      ).catch((error) => {
        logger.log({ level: 'warn', message: `Failed to locate MXF footer after last frame: ${exceptionToUserFriendlyMessage(NO_LOGGER, error)}` });
        return null;
      });
  }

  if (footer === null) {
    logger.log({ level: 'warn', message: 'Unable to fetch footer from MXF. Rewriting the header into a footer instead' });
    footer = deriveFooterFromHeader(header);
  }

  return { header, footer, indexTableSegment };
}

export async function fetchIndex(services: { resourceAccessor: ResourceAccessor }, index: ResourceLocation): Promise<MimirIndexFile> {
  const openResource = await services.resourceAccessor.open(index, 0, null);
  const bytes = await readWholeStream(openResource.stream);
  return parseMimirIndexFile(bytes);
}
